import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../redux/store';
import {UserViewType} from '../types/TypesUser';

export type UserStatus = UserViewType['status'];

interface StatusRestriction {
	canShowAllPages: boolean;
	canShowDynamicGraphs: boolean;
	canShowDynamicLiders: boolean;
	canShowDynamicHistory: boolean;
	canShowDynamicRecommendations: boolean;
	canShowNetworkIncome: boolean;
	canShowPremiaBlock: boolean;
	canShowCashbackBlock: boolean;
	canShowOrdersByOthers: boolean;
	canShowDeferredPoints: boolean;
	canShowMainRecommendations: boolean;
	canShowMainReferral: boolean;
	canShowMainGraphs: boolean;
	canShowMainStat2: boolean;
	canShowNetworkClients: boolean;
	canShowReceiptIncome: boolean;
	canAccessNetworkIncomePage: boolean;
	canAccessDynamicPage: boolean;
	canShowMainNetworkIncomeAction: boolean;
	canShowMainStat2Links: boolean;
	canShowMainDynamicLink: boolean;
	canAccessBusinessSection: boolean;
	canAccessMyTeamPage: boolean;
	canAccessPartnerIncomePage: boolean;
	canAccessMyOrdersPage: boolean;
	canAccessWarehouseSection: boolean;
	canAccessFinancialReport: boolean;
	canAccessCashOrders: boolean;
	isTerminateUser: boolean;
}

const createRestriction = (overrides: Partial<StatusRestriction> = {}): StatusRestriction => ({
	canShowAllPages: false,
	canShowDynamicGraphs: false,
	canShowDynamicLiders: false,
	canShowDynamicHistory: false,
	canShowDynamicRecommendations: false,
	canShowNetworkIncome: false,
	canShowPremiaBlock: false,
	canShowCashbackBlock: false,
	canShowOrdersByOthers: false,
	canShowDeferredPoints: false,
	canShowMainRecommendations: false,
	canShowMainReferral: false,
	canShowMainGraphs: false,
	canShowMainStat2: false,
	canShowNetworkClients: false,
	canShowReceiptIncome: false,
	canAccessNetworkIncomePage: false,
	canAccessDynamicPage: false,
	canShowMainNetworkIncomeAction: false,
	canShowMainStat2Links: false,
	canShowMainDynamicLink: false,
	canAccessBusinessSection: false,
	canAccessMyTeamPage: false,
	canAccessPartnerIncomePage: false,
	canAccessMyOrdersPage: false,
	canAccessWarehouseSection: false,
	canAccessFinancialReport: false,
	canAccessCashOrders: false,
	isTerminateUser: false,
	...overrides,
});

const fullAccessRestriction = createRestriction({
	canShowAllPages: true,
	canShowDynamicGraphs: true,
	canShowDynamicLiders: true,
	canShowDynamicHistory: true,
	canShowDynamicRecommendations: true,
	canShowNetworkIncome: true,
	canShowPremiaBlock: true,
	canShowCashbackBlock: true,
	canShowOrdersByOthers: true,
	canShowDeferredPoints: true,
	canShowMainRecommendations: true,
	canShowMainReferral: true,
	canShowMainGraphs: true,
	canShowMainStat2: true,
	canShowNetworkClients: true,
	canShowReceiptIncome: true,
	canAccessNetworkIncomePage: true,
	canAccessDynamicPage: true,
	canShowMainNetworkIncomeAction: true,
	canShowMainStat2Links: true,
	canShowMainDynamicLink: true,
	canAccessBusinessSection: true,
	canAccessMyTeamPage: true,
	canAccessPartnerIncomePage: true,
	canAccessMyOrdersPage: true,
	canAccessWarehouseSection: true,
	canAccessFinancialReport: true,
	canAccessCashOrders: true,
});

export const useStatusRestriction = (): StatusRestriction => {
	const sessionRoleId = useSelector(
		(state: RootState) => state.appData.session.role_id,
	);
	const authDataRoleId = useSelector(
		(state: RootState) => state.userData.authData.role_id,
	);
	const userStatus = useSelector(
		(state: RootState) => state.userData.userView?.status,
	);

	return useMemo(() => {
		const status = userStatus as UserStatus | undefined;
		const rawRoleId = sessionRoleId ?? authDataRoleId ?? null;
		const normalizedRoleId = rawRoleId === null ? 2 : rawRoleId;

		if (normalizedRoleId === 5) {
			return createRestriction({
				isTerminateUser: true,
				canAccessBusinessSection: false,
				canAccessWarehouseSection: false,
			});
		}

		let baseRestriction: StatusRestriction;

		if (normalizedRoleId === 3 || normalizedRoleId === 1) {
			baseRestriction = fullAccessRestriction;
		} else if (normalizedRoleId === 4) {
			baseRestriction = {
				...fullAccessRestriction,
				canAccessBusinessSection: false,
				canAccessMyTeamPage: false,
				canAccessPartnerIncomePage: false,
				canAccessMyOrdersPage: false,
				canAccessNetworkIncomePage: false,
				canAccessDynamicPage: false,
				canShowMainNetworkIncomeAction: false,
				canShowMainStat2: false,
				canShowMainStat2Links: false,
				canShowMainGraphs: false,
				canShowMainRecommendations: false,
				canShowMainReferral: false,
				canShowDynamicGraphs: false,
				canShowDynamicLiders: false,
				canShowDynamicHistory: false,
				canShowDynamicRecommendations: false,
				canShowNetworkIncome: false,
				canShowPremiaBlock: false,
				canShowCashbackBlock: false,
				canShowOrdersByOthers: false,
				canShowDeferredPoints: false,
				canShowNetworkClients: false,
				canShowReceiptIncome: false,
				canShowMainDynamicLink: false,
				canAccessWarehouseSection: true,
				canAccessFinancialReport: false,
				canAccessCashOrders: false,
			};
		} else {
			baseRestriction = {
				...fullAccessRestriction,
				canAccessWarehouseSection: false,
				canAccessFinancialReport: false,
				canAccessCashOrders: false,
			};
		}

		if (!status) {
			return baseRestriction;
		}

		let restriction = {...baseRestriction};

		const canUseBusinessFeatures = restriction.canAccessBusinessSection;
		const canUseDynamicPage = restriction.canAccessDynamicPage;
		const canUseNetworkIncome = restriction.canAccessNetworkIncomePage;

		switch (status) {
			case 'lider':
			case 'manager': {
				restriction = {
					...restriction,
					canShowDynamicGraphs: canUseDynamicPage,
					canShowDynamicLiders: canUseDynamicPage,
					canShowDynamicHistory: canUseDynamicPage,
					canShowDynamicRecommendations: canUseDynamicPage,
					canShowNetworkIncome: canUseNetworkIncome,
					canShowReceiptIncome: canUseNetworkIncome,
					canShowPremiaBlock: true,
					canShowCashbackBlock: true,
					canShowOrdersByOthers: true,
					canShowDeferredPoints: true,
					canShowMainRecommendations: true,
					canShowMainReferral: true,
					canShowMainGraphs: canUseBusinessFeatures,
					canShowMainStat2: canUseBusinessFeatures,
					canShowMainStat2Links: restriction.canAccessMyTeamPage,
					canShowMainNetworkIncomeAction:
						canUseBusinessFeatures && canUseNetworkIncome,
					canShowMainDynamicLink: canUseDynamicPage,
					canShowNetworkClients: canUseDynamicPage,
				};
				break;
			}
			case 'business': {
				restriction = {
					...restriction,
					canShowDynamicGraphs: false,
					canShowDynamicLiders: false,
					canShowDynamicHistory: canUseDynamicPage,
					canShowNetworkClients: false,
				};
				break;
			}
			case 'loyalty': {
				restriction = {
					...restriction,
					canShowDynamicGraphs: false,
					canShowDynamicLiders: false,
					canShowDynamicHistory: false,
					canShowDynamicRecommendations: canUseDynamicPage,
					canShowNetworkIncome: false,
					canShowReceiptIncome: false,
					canShowPremiaBlock: false,
					canShowCashbackBlock: false,
					canShowOrdersByOthers: false,
					canShowDeferredPoints: false,
					canShowMainRecommendations: true,
					canShowMainReferral: true,
					canShowMainGraphs: false,
					canShowMainStat2: false,
					canShowMainNetworkIncomeAction: false,
					canShowMainStat2Links: false,
					canShowMainDynamicLink: false,
					canShowNetworkClients: false,
					canAccessNetworkIncomePage: false,
				};
				break;
			}
			case 'optima':
			case 'newbie': {
				restriction = {
					...restriction,
					canShowDynamicGraphs: false,
					canShowDynamicLiders: false,
					canShowDynamicHistory: false,
					canShowDynamicRecommendations: false,
					canShowNetworkIncome: false,
					canShowReceiptIncome: false,
					canShowPremiaBlock: false,
					canShowCashbackBlock: false,
					canShowOrdersByOthers: false,
					canShowDeferredPoints: false,
					canShowMainRecommendations: true,
					canShowMainReferral: true,
					canShowMainGraphs: false,
					canShowMainStat2: true,
					canShowMainNetworkIncomeAction: false,
					canShowMainStat2Links: false,
					canShowMainDynamicLink: false,
					canShowNetworkClients: false,
					canAccessNetworkIncomePage: false,
					canAccessDynamicPage: false,
				};
				break;
			}
			default:
				break;
		}

		return restriction;
	}, [sessionRoleId, authDataRoleId, userStatus]);
};

